/*
Copyright 2026 Chain Care.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import type {
  PrenigmaCollaborationEnvelope,
  PrenigmaCollaborationRuntimeEventSink,
  PrenigmaCollaborationRuntimeEventSinkContext,
} from "./collaborationRuntime";

export interface PrenigmaCollaborationRuntimeMatrixEvent {
  getContent: () => unknown;
  getId: () => string | undefined;
  getType: () => string;
}

export interface PrenigmaCollaborationRuntimeMatrixRoom {
  client: {
    sendEvent: (
      roomId: string,
      eventType: string,
      content: Record<string, unknown>,
      txnId?: string,
    ) => Promise<{ event_id?: string }>;
  };
  getLiveTimeline?: () => {
    getEvents?: () => PrenigmaCollaborationRuntimeMatrixEvent[];
  };
  roomId: string;
}

export interface PrenigmaCollaborationRuntimeMatrixPublication {
  matrixEventId: string;
  matrixEventType: string;
  matrixReadbackVerified: boolean;
  matrixRoomId: string;
}

export interface CreatePrenigmaCollaborationRuntimeMatrixSinkOptions {
  nextSink: PrenigmaCollaborationRuntimeEventSink;
  room: PrenigmaCollaborationRuntimeMatrixRoom;
}

export function createPrenigmaCollaborationRuntimeMatrixEventSink({
  nextSink,
  room,
}: CreatePrenigmaCollaborationRuntimeMatrixSinkOptions): PrenigmaCollaborationRuntimeEventSink {
  return async (
    event: PrenigmaCollaborationEnvelope,
    context: PrenigmaCollaborationRuntimeEventSinkContext = {},
  ): Promise<void> => {
    const publication = await publishPrenigmaCollaborationSourceEventToMatrix(
      room,
      event,
    );
    await nextSink(event, {
      ...context,
      matrixEventIds: [
        ...new Set([
          ...(context.matrixEventIds ?? []),
          publication.matrixEventId,
        ]),
      ],
      matrixEventType: publication.matrixEventType,
      matrixReadbackVerified: publication.matrixReadbackVerified,
      matrixRoomId: publication.matrixRoomId,
    });
  };
}

export async function publishPrenigmaCollaborationSourceEventToMatrix(
  room: PrenigmaCollaborationRuntimeMatrixRoom,
  event: PrenigmaCollaborationEnvelope,
): Promise<PrenigmaCollaborationRuntimeMatrixPublication> {
  const matrixEventType = event.schema;
  const response = await room.client.sendEvent(
    room.roomId,
    matrixEventType,
    {
      schema: event.schema,
      event,
      source_event_id: event.source_event_id,
      source_event_type: event.event_type,
      source_event_event_id: event.event_id,
      raw_secret_material_returned: false,
    },
    `prenigma-source-${event.event_id}`,
  );
  const matrixEventId = response.event_id ?? "";
  if (!matrixEventId) {
    throw new Error("Prenigma Matrix source-event publication returned no event id");
  }

  return {
    matrixEventId,
    matrixEventType,
    matrixReadbackVerified: matrixTimelineContainsSourceEvent(
      room,
      matrixEventId,
      matrixEventType,
      event,
    ),
    matrixRoomId: room.roomId,
  };
}

function matrixTimelineContainsSourceEvent(
  room: PrenigmaCollaborationRuntimeMatrixRoom,
  matrixEventId: string,
  matrixEventType: string,
  event: PrenigmaCollaborationEnvelope,
): boolean {
  const events = room.getLiveTimeline?.().getEvents?.() ?? [];
  return events.some((candidate) => {
    const content = matrixContent(candidate.getContent());
    return (
      candidate.getId() === matrixEventId &&
      candidate.getType() === matrixEventType &&
      content.schema === event.schema &&
      content.source_event_id === event.source_event_id
    );
  });
}

function matrixContent(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}
